/**
 * @file ReturnKanbanCard.tsx
 * @description Карточка возврата на Kanban-доске (phase10.5 шаг 3).
 *
 * Зеркало OrderKanbanCard. Border-left = цвет стадии. Показывает:
 *  - номер исходного заказа (Z-NNN),
 *  - имя клиента не показываем (внутри Return нет — будет в детали),
 *  - тип возврата (label),
 *  - сумма refund + кол-во позиций.
 */

import { useDraggable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { Package, Undo2 } from "lucide-react";

import { Badge } from "../ui/badge";
import { formatMoney, RETURN_TYPE_LABEL } from "../../lib/api-orders";
import type { ReturnDto } from "../../lib/api-returns";
import { safeStageColor } from "../../lib/stage-color";

interface Props {
  ret: ReturnDto;
  /** Если true — рендер в DragOverlay. */
  dragging?: boolean;
  onClick?: () => void;
}

export function ReturnKanbanCard({ ret, dragging, onClick }: Props) {
  const draggable = useDraggable({
    id: ret.id,
    data: {
      type: "return",
      returnId: ret.id,
      fromStageId: ret.stage?.id ?? null,
    },
    disabled: dragging,
  });

  // Цвет магазина уходит в `style` — только проверенный hex.
  const stageColor = safeStageColor(ret.stage?.color);
  const style: React.CSSProperties = dragging
    ? {
        cursor: "grabbing",
        ...(stageColor ? { borderLeft: `4px solid ${stageColor}` } : {}),
      }
    : {
        transform: CSS.Translate.toString(draggable.transform),
        opacity: draggable.isDragging ? 0 : 1,
        ...(stageColor ? { borderLeft: `4px solid ${stageColor}` } : {}),
      };

  return (
    <div
      ref={dragging ? undefined : draggable.setNodeRef}
      style={style}
      {...(dragging ? {} : draggable.attributes)}
      {...(dragging ? {} : draggable.listeners)}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
      role="button"
      tabIndex={0}
      className={`overflow-hidden rounded-lg bg-surface-lowest p-3 shadow-sm cursor-grab active:cursor-grabbing select-none transition-shadow hover:shadow ${
        dragging ? "rotate-2 shadow-lg" : ""
      }`}
    >
      <div className="flex items-center gap-2">
        <Undo2 className="size-3.5 text-on-surface-variant shrink-0" />
        <span className="type-body truncate font-medium tabular-nums">
          {ret.orderNumber}
        </span>
      </div>
      <div className="type-caption mt-1 truncate text-on-surface-variant">
        {RETURN_TYPE_LABEL[ret.type]}
      </div>
      <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
        <span className="type-body font-semibold tabular-nums text-on-surface">
          {formatMoney(ret.refundAmount)} ₽
        </span>
        <Badge variant="outline" className="font-normal">
          <Package className="size-3 mr-0.5" />
          {ret.itemsCount}
        </Badge>
      </div>
      {ret.customerComment ? (
        <div className="type-caption mt-1 truncate italic text-on-surface-variant">
          {ret.customerComment}
        </div>
      ) : null}
    </div>
  );
}
